import React, { useState, useEffect } from 'react';
import { useChat } from '../../Context/ChatContext';
import { useAuth } from '../../Context/AuthContext';
import { useTheme } from '../../Context/ThemeContext'; // Add this import
import { getUserConnections, getUsersPresenceListener } from '../../services/userService'; // Adjust path
import Loader from '../Loader'; // Adjust path
import { formatDistanceToNowStrict } from 'date-fns'; // For formatting lastSeen

const ChatLauncher = () => {
  const { openChatWithUser } = useChat(); // Get connections from context if available
  const { currentUser } = useAuth();
  const [connections, setConnections] = useState([]); // Initialize with context connections
  const [presenceData, setPresenceData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isDarkMode } = useTheme(); // Get theme state

  // Fetch initial connections
  useEffect(() => {
    if (!currentUser?.uid) {
      setConnections([]); // Clear connections if user logs out
      return;
    }

    const fetchConnections = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedConnections = await getUserConnections(currentUser.uid);
        if (Array.isArray(fetchedConnections)) {
          setConnections(fetchedConnections);
        } else {
          console.error("Fetched connections is not an array:", fetchedConnections);
          setConnections([]);
          setError("Failed to load connections data.");
        }
      } catch (err) {
        console.error("Error in fetchConnections:", err);
        setError("Failed to load connections.");
        setConnections([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConnections();
  }, [currentUser?.uid]); // Re-run only when user changes

  // --- Optional: Real-time presence listener ---
  useEffect(() => {
    if (connections.length === 0) return () => {}; // No connections to listen for

    const connectionIds = connections.map(c => c.userId);

    const handlePresenceUpdate = (updatedPresence) => {
      setPresenceData(prev => ({ ...prev, ...updatedPresence }));
    };

    const unsubscribe = getUsersPresenceListener(connectionIds, handlePresenceUpdate);

    return () => {
      unsubscribe();
    };
  }, [connections]); // Re-run when the list of connections changes

  const handleSelectUser = (user) => {
    // Ensure user object is valid and has a userId
    if (user && user.userId) {
      openChatWithUser(user);
    } else {
      console.error("Invalid user object passed to handleSelectUser:", user);
    }
  };

  // Helper to format lastSeen
  const formatLastSeen = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'unknown';
    try {
      return formatDistanceToNowStrict(timestamp.toDate(), { addSuffix: true });
    } catch (e) {
      return 'unknown';
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-full"><Loader /></div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-600">{error}</div>;
  }

  return (
    <div className={`flex flex-col h-full ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <div className="flex-grow overflow-y-auto">
        {connections.length === 0 ? (
          <div className={`text-center p-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No connections yet. Connect with others to start chatting!
          </div>
        ) : (
          <ul className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {connections.map((conn) => {
              if (!conn || !conn.userId) return null; // Skip invalid

              // Get presence status: prioritize real-time update, fallback to initial fetch
              const userPresence = presenceData[conn.userId];
              const isOnline = userPresence ? userPresence.isOnline : conn.isOnline;
              const lastSeen = userPresence ? userPresence.lastSeen : conn.lastSeen;

              return (
                <li
                  key={conn.userId}
                  onClick={() => handleSelectUser(conn)}
                  className={`p-3 flex items-center space-x-3 cursor-pointer transition-colors duration-150 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  <div className="relative flex-shrink-0">
                    <img
                      src={conn.profilePicUrl || '/default-profile.jpg'}
                      alt={conn.name || 'User'}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    {/* Online Indicator */}
                    {isOnline && (
                      <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-gray-900"></span>
                    )}
                  </div>
                  <div className="flex-grow min-w-0">
                    <span className={`font-medium text-sm block truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{conn.name || 'Unknown User'}</span>
                    {/* Display Last Seen if not Online */}
                    {!isOnline && lastSeen && (
                      <span className="text-xs text-gray-400 block">
                        {`Last seen ${formatLastSeen(lastSeen)}`}
                      </span>
                    )}
                    {!isOnline && !lastSeen && (
                      <span className={`text-xs block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Offline
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ChatLauncher;
